import DataTable, {type DataTableRef} from 'datatables.net-react'
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT, {type Config} from 'datatables.net';
import {formattedDate} from "../../util/ReplaceText.tsx";
import type {CloudInit} from "../../lib/ModelTypes.ts";
import React, {forwardRef, type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import {cloud_init_api, CloudInitAPIConnectorError} from "../../lib/CloudInit.ts";
import {useNavigate} from "react-router-dom";
import {swal_util} from "../../lib/SwalUtil.ts";

import {frontBaseUrl} from "../../build_constants.ts";
import {useTranslation} from "react-i18next";


export interface CloudInitDataRef {
    getSelectedData: () => CloudInit[];
    redraw: () => void;
    onClickDelete: (name: string) => void;
}

type DataTableProps = {
    ref?: Ref<CloudInitDataRef>;
};


const CloudInitTable = forwardRef<CloudInitDataRef, DataTableProps>(
    (_props, ref) => {
        const {t} = useTranslation();

        DataTable.use(DT);
        const navigate = useNavigate();
        const [data, setData] = useState<{
            cloud_init: CloudInit[];
            checked: { [key: string]: boolean };
            dataLoading: boolean;
        }>({
            cloud_init: [],
            checked: {},
            dataLoading: true,
        });
        const dataTableRef = useRef<DataTableRef>(null);

        useImperativeHandle(ref, () => ({
            getSelectedData: () => data.cloud_init.filter((row) => data.checked[row.name]),
            redraw: () => redraw().finally(),
            onClickDelete: (name: string) => {
                onClickDelete({
                    target: {
                        dataset: {
                            name: name,
                        },
                    }
                } as unknown as Event).finally()
            },
        }));

        const redraw = useCallback(async () => {
            try {
                const cloud_init = await cloud_init_api.get();
                if (cloud_init) {
                    setData(prev => ({...prev, cloud_init: cloud_init.data}));
                    setData(prev => ({...prev, dataLoading: false}));
                }
            } catch (err) {
                await swal_util.show_error(`${t("CloudInit.errorLoadYaml")} ${t("common.adminContact")}`);
                console.error(err);
            }
        }, [t]);

        const onClickDelete = useCallback(async (e: Event) => {
            const target = (e.target ?? e.currentTarget) as HTMLElement;
            const name = target.dataset.name;

            if (!name) {
                console.log(target);
                console.error("name is null");
                await swal_util.show_error("name is null");
                return;
            }

            try {
                await cloud_init_api.delete(name);
                await redraw();
            } catch (err) {
                if (err instanceof CloudInitAPIConnectorError) {
                    await swal_util.show_error(`${err.localization}`);
                } else {
                    await swal_util.show_error(`${t("CloudInit.errorConfigYaml")} ${t("common.adminContact")}`);
                }
                console.error(err);
            }
        }, [redraw, t]);

        const onClickModify = useCallback(async (e: Event) => {
            const target = (e.target ?? e.currentTarget) as HTMLElement;
            const name = target.dataset.name;

            if (!name) {
                console.log(target);
                console.error("name is null");
                await swal_util.show_error("name is null");
                return;
            }

            try {
                navigate(`${frontBaseUrl}/cloud_init/edit/${name}`);
            } catch (err) {
                await swal_util.show_error(`${t("CloudInit.errorLoadDataYaml")} ${t("common.adminContact")}`);
                console.log(err);
            }
        }, [navigate, t]);


        const onClickSetDefault = useCallback(async (e: Event) => {
            const target = (e.target ?? e.currentTarget) as HTMLElement;
            const name = target.dataset.name;

            if (!name) {
                console.log(target);
                console.error("name is null");
                await swal_util.show_error("name is null");
                return;
            }

            try {
                await cloud_init_api.setDefault(name);
                await redraw();
            } catch (err) {
                if (err instanceof CloudInitAPIConnectorError) {
                    await swal_util.show_error(`${err.localization}`);
                } else {
                    await swal_util.show_error(`${t("CloudInit.errorConfigYaml")} ${t("common.adminContact")}`);
                }
                console.error(err);
            }
        }, [redraw, t]);

        function handleSelectAll(e: React.UIEvent) {
            const otherCheckboxes = document.getElementsByClassName("check-cloud-init");
            Array.from(otherCheckboxes).forEach((element) => {
                const checkbox = element as HTMLInputElement;
                checkbox.checked = (e.target as HTMLInputElement).checked;
            });
            data.cloud_init.forEach((item) => {
                handleCheckboxChange(item.name, (e.target as HTMLInputElement).checked)
            })
        }

        function handleCheckboxChange(name: string, checked: boolean) {
            setData((prevData) => ({
                ...prevData,
                checked: {
                    ...prevData.checked,
                    [name]: checked,
                },
            }));
        }

        const columns = [
            {
                data: 'name',
                orderable: false,
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                render: (name: string) => {
                    const checked = data.checked[name] ?? false;
                    return `<input type="checkbox" class="check-cloud-init form-check-input" data-id="${name}" value="${name}" ${checked && "checked"} >`;
                },
            },
            {data: 'name'},
            {
                data: 'created_date', render: (data: string) => {
                    return formattedDate(data);
                }
            },
            {
                data: 'modified_date', render: (data: string) => {
                    return formattedDate(data);
                }
            },
            {
                className: "text-center",
                render: (_data: string, _type: string, row: CloudInit) => {
                    const defaultLabel = t(row.default ? "common.default" : "common.setDefault");
                    const modifyText = t("common.edit");
                    const deleteText = t("common.delete");

                    const btn_default = `<button class="btn ${row.default ? "btn-primary" : "btn-outline-primary default"}" data-id="${row.id}" data-name="${row.name}" style="font-size: 14px">${defaultLabel}</button>`;
                    const btn_modify = `<button class="btn btn-primary modify" data-id="${row.id}" data-name="${row.name}" style="font-size: 14px">${modifyText}</button>`;
                    const btn_delete = `<button class="btn btn-danger delete" data-id="${row.id}"  data-name="${row.name}" style="font-size: 14px">${deleteText}</button>`;
                    return `${btn_default} ${btn_modify} ${btn_delete}`
                },
                orderable: false,
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            }
        ];

        const options: Config = {
            destroy: true,

            paging: false,
            searching: false,
            order: [[1, "asc"]],
            ordering: true,
            lengthChange: false,
            info: false,
        };


        useEffect(() => {
                // 화면이 다시 그려질때 dom을 바인딩하여야 react가 정상 동작
                // option 의 createdRow를 사용하면 안됨

                const tableNodeApi = dataTableRef.current?.dt();
                const rowsApi = tableNodeApi?.rows;
                if (!rowsApi) return;
                const count = rowsApi().count();

                const buttonsSet: {
                    checkbox: Element | null;
                    deleteButton: Element | null;
                    modifyButton: Element | null;
                    defaultButton: Element | null;
                }[] = [];
                for (let i = 0; i < count; i++) {
                    const headCell = tableNodeApi.cell(i, 0).node();

                    const checkbox = headCell.querySelector("input[type=checkbox]") as HTMLInputElement;

                    const actionCell = tableNodeApi.cell(i, 4).node();
                    const deleteButton = actionCell.querySelector('.delete');
                    const modifyButton = actionCell.querySelector('.modify');
                    const defaultButton = actionCell.querySelector('.default');

                    deleteButton?.addEventListener?.('click', onClickDelete);
                    modifyButton?.addEventListener?.('click', onClickModify);
                    defaultButton?.addEventListener?.('click', onClickSetDefault);

                    checkbox.onchange = () => {
                        handleCheckboxChange(checkbox.value, checkbox.checked);
                    }

                    buttonsSet.push({
                        checkbox,
                        deleteButton,
                        modifyButton,
                        defaultButton,
                    });
                }

                return () => {
                    buttonsSet.forEach(({deleteButton, modifyButton, defaultButton}) => {
                        deleteButton?.removeEventListener("click", onClickDelete);
                        modifyButton?.removeEventListener("click", onClickModify);
                        defaultButton?.removeEventListener("click", onClickSetDefault);
                    });
                }
            }
        );

        return (
            <div style={{fontSize: "14px"}}>
                <DataTable ref={dataTableRef} data={data.cloud_init} columns={columns} className="display"
                           options={options}>
                    <thead>
                    <tr>
                        <th className="text-center">
                            <input className="form-check-input" type={"checkbox"}
                                   onClick={(e) => handleSelectAll(e)}/></th>
                        <th>{t("CloudInit.YamlName")}</th>
                        <th>{t("common.startDate")}</th>
                        <th>{t("common.modified")}</th>
                        <th>{t("common.action")}</th>
                    </tr>
                    </thead>
                    <tfoot>
                    <tr>
                        <td colSpan={columns.length} className="text-start text-secondary border-bottom">
                            {data.dataLoading ? <span>{t("common.loadingList")}</span> :
                                <span>{data.cloud_init.length} {t("CloudInit.YAMLFileCount")}</span>
                            }
                        </td>
                    </tr>
                    </tfoot>
                </DataTable>
            </div>
        );
    });

export default CloudInitTable;